import { API_BASE_URL } from '../config';
import React, { useEffect, useRef, useState } from 'react';

async function authorizedGet(path) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Sign in again to load the list.');
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Could not load the list.');
  }
  return data;
}

export async function loadPatientOptions() {
  const data = await authorizedGet('/patients');
  return (Array.isArray(data) ? data : []).map((patient) => ({
    key: patient._id || patient.PATIENT_ID,
    label: [patient.PATIENT_NAME, patient.PATIENT_ID, patient.PATIENT_PHONE].filter(Boolean).join(' · '),
    item: patient,
  }));
}

export async function loadDoctorOptions() {
  const data = await authorizedGet('/doctors');
  return (Array.isArray(data) ? data : []).map((doctor) => ({
    key: doctor._id || doctor.DOCTOR_ID,
    label: doctor.DOCTOR_NAME || doctor.DOCTOR_ID,
    item: doctor,
  }));
}

export async function loadMedicineOptions() {
  const data = await authorizedGet('/medicines');
  return (Array.isArray(data) ? data : []).map((medicine) => ({
    key: medicine._id || medicine.MEDICINE_ID,
    label: [medicine.MEDICINE_NAME, medicine.STRENGTH, medicine.QUANTITY_IN_STOCK != null ? `Stock ${medicine.QUANTITY_IN_STOCK}` : '']
      .filter(Boolean)
      .join(' · '),
    item: medicine,
  }));
}

export async function loadAppointmentNameOptions() {
  const data = await authorizedGet('/appointments');
  const names = new Set();
  (Array.isArray(data) ? data : []).forEach((appointment) => {
    const patient = appointment.PATIENT_ID?.PATIENT_NAME || appointment.PATIENT_NAME;
    const doctor = appointment.DOCTOR_ID?.DOCTOR_NAME || appointment.DOCTOR_NAME;
    if (patient) names.add(patient);
    if (doctor) names.add(doctor);
  });
  return [...names].sort().map((name) => ({ key: name, label: name, item: name }));
}

export function appointmentPatientOptions(appointments) {
  const seen = new Set();
  const options = [];
  appointments
    .filter((appointment) => appointment.APPOINTMENT_STATUS === 'Completed')
    .forEach((appointment) => {
      const patient = appointment.PATIENT_ID || {};
      const name = patient.PATIENT_NAME;
      if (!name || seen.has(name)) return;
      seen.add(name);
      options.push({
        key: patient._id || name,
        label: [name, patient.PATIENT_ID, patient.PATIENT_PHONE].filter(Boolean).join(' · '),
        item: name,
      });
    });
  return options;
}

const SearchBox = ({
  value,
  onChange,
  placeholder,
  name,
  onKeyDown,
  options,
  loadOptions,
  onSelect,
  filterText,
}) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(options || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [menuBox, setMenuBox] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (options) setItems(options);
  }, [options]);

  useEffect(() => {
    if (!open) return undefined;
    const placeMenu = () => {
      const node = inputRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      setMenuBox({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    const close = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    placeMenu();
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || !loadOptions) return;
    setLoading(true);
    setError('');
    try {
      setItems(await loadOptions());
    } catch (err) {
      setItems([]);
      setError(err.message || 'Could not load the list.');
    } finally {
      setLoading(false);
    }
  };

  const query = (filterText !== undefined ? filterText : value || '').trim().toLowerCase();
  const shown = items.filter((item) => !query || item.label.toLowerCase().includes(query));

  return (
    <div ref={boxRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: '0.35rem' }}>
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            padding: '0.65rem 0.75rem',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '1rem',
            boxSizing: 'border-box',
            backgroundColor: 'white',
          }}
        />
        <button
          type="button"
          aria-label="Show choices"
          aria-expanded={open}
          onClick={toggle}
          style={{
            padding: '0 0.85rem',
            border: '1px solid #1f4d40',
            borderRadius: '4px',
            background: open ? '#1f4d40' : '#fff',
            color: open ? '#fff' : '#1f4d40',
            cursor: 'pointer',
            fontSize: '0.95rem',
          }}
        >
          ▼
        </button>
      </div>
      {open && (
        <ul
          style={{
            position: 'fixed',
            zIndex: 1300,
            top: menuBox ? menuBox.top : 0,
            left: menuBox ? menuBox.left : 0,
            width: menuBox ? menuBox.width : undefined,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            background: '#fff',
            border: '1px solid #d5ded9',
            borderRadius: '6px',
            boxShadow: '0 8px 24px rgba(22, 56, 47, 0.12)',
            maxHeight: '280px',
            overflowY: 'auto',
          }}
        >
          {loading && <li style={{ padding: '0.75rem', color: '#5c6b64' }}>Loading...</li>}
          {error && <li style={{ padding: '0.75rem', color: '#8a2430' }}>{error}</li>}
          {!loading && !error && shown.length === 0 && (
            <li style={{ padding: '0.75rem', color: '#5c6b64' }}>No matches</li>
          )}
          {!loading && !error && shown.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => {
                  onSelect(item);
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.65rem 0.75rem',
                  border: 'none',
                  borderBottom: '1px solid #eef2f0',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBox;
